import { ListOrderedIcon } from "lucide-react";

import { DetailSection } from "@/components/show";
import {
  LeadersCard,
  LeagueStatsCard,
} from "@/pages/leagues/components/league-extras";
import { StandingsTable } from "@/pages/leagues/components/standings-table";
import { leagueStandingsViewSchema } from "@/pages/leagues/schemas/league-standings.view";
import { DetailNotice } from "@/pages/sports/components/detail-notice";
import { AfterMatchNote } from "@/pages/sports/components/read-only-note";
import { SectionBoundary } from "@/pages/sports/components/section-boundary";
import { useSportsDetail } from "@/pages/sports/hooks/use-sports-detail";
import type { IStandingsGroup } from "@/types/league-standings";

/** "RegularSeason" becomes "Regular season". */
const humanize = (phase: string) =>
  phase
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

const groupHeading = (group: IStandingsGroup, index: number) =>
  typeof group.name === "string" && group.name
    ? group.name
    : typeof group.phase === "string" && group.phase
      ? humanize(group.phase)
      : `Table ${index + 1}`;

/**
 * The league's current-season table, league stats and (football) top scorers and assists,
 * read from `GET /api/leagues/{id}/standings` (the backend defaults the season to the
 * league's current one). Each block renders only when it has data. Admin.API answers 404
 * for a league with no table for that season, which reads as "no standings yet"; an empty
 * `groups` list (the BFF's answer for a missing season) is treated the same way. A wrongly
 * typed value in the payload falls back to one muted line instead of blanking the page.
 */
export function LeagueStandingsSection({ leagueId }: { leagueId: string }) {
  return (
    <SectionBoundary key={leagueId} what="standings">
      <LeagueStandings leagueId={leagueId} />
    </SectionBoundary>
  );
}

function LeagueStandings({ leagueId }: { leagueId: string }) {
  const state = useSportsDetail(
    `leagues/${leagueId}/standings`,
    leagueStandingsViewSchema,
    "league standings",
  );
  if (state.status !== "ready") {
    return (
      <DetailNotice
        state={state}
        what="standings"
        emptyText="No standings yet"
      />
    );
  }

  const { season, stats, leaders } = state.data;
  const groups = (state.data.groups ?? []).filter((g) => g.rows?.length);
  const scorers = leaders?.goals ?? [];
  const assists = leaders?.assists ?? [];

  if (!groups.length && !stats && !scorers.length && !assists.length) {
    return (
      <DetailNotice
        state={{ status: "empty" }}
        what="standings"
        emptyText="No standings yet"
      />
    );
  }

  return (
    <section className="flex flex-col gap-4">
      {groups.length > 0 && (
        <DetailSection
          title={season ? `Standings · ${season}` : "Standings"}
          icon={<ListOrderedIcon className="size-4" />}
          contentClassName="flex flex-col gap-6"
        >
          {groups.map((group, i) => (
            <div key={i} className="flex flex-col gap-2">
              {groups.length > 1 && (
                <h3 className="text-sm font-semibold">
                  {groupHeading(group, i)}
                </h3>
              )}
              <StandingsTable rows={group.rows ?? []} />
            </div>
          ))}
        </DetailSection>
      )}
      {stats && <LeagueStatsCard stats={stats} />}
      {(scorers.length > 0 || assists.length > 0) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {scorers.length > 0 && (
            <LeadersCard
              title="Top scorers"
              valueLabel="Goals"
              rows={scorers}
            />
          )}
          {assists.length > 0 && (
            <LeadersCard
              title="Top assists"
              valueLabel="Assists"
              rows={assists}
            />
          )}
        </div>
      )}
      <AfterMatchNote />
    </section>
  );
}
